/* TODO: Fix the types for the Table component */

import {
  ChevronDownIcon,
  Cog8ToothIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { Checkbox } from '@praetorian-chariot/ui';
import { notUndefined, useVirtualizer } from '@tanstack/react-virtual';
import { startCase } from 'lodash';
import { Slash } from 'lucide-react';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/form/Input';
import {
  CELL_WIDTHS,
  ROW_HEIGHT,
  SCROLL_BUFFER_ROW_COUNT,
} from '@/components/table/constants';
import { useResponsiveColumns } from '@/components/table/hooks/useResponsiveColumns';
import { InfiniteScrollIndicator } from '@/components/table/InfiniteScrollIndicator';
import { SortableColumns } from '@/components/table/sortableColumns';
import { SortColumn } from '@/components/table/SortColumn';
import { TableBody } from '@/components/table/TableBody';
import {
  CellAlignment,
  Column,
  InternalTData,
  TableProps,
} from '@/components/table/types';
import { applyColumnWidth } from '@/components/table/utils/columnWidth';
import { Tooltip } from '@/components/Tooltip';
import { NoData } from '@/components/ui/NoData';
import { useScroll } from '@/hooks';
import { useResize } from '@/hooks/useResize';
import { RenderHeaderExtraContentSection } from '@/sections/AuthenticatedApp';
import { useGlobalState } from '@/state/global.state';
import { cn } from '@/utils/classname';

/**
 * Generate a stable, unique key for table column headers.
 * Uses id (preferred), then label (if string), then generic, plus index for uniqueness.
 */
function getColumnKey<TData>(
  tableName: string,
  column: Column<TData>,
  index: number
): string {
  const identifier =
    (column.id ? String(column.id) : '') ||
    (typeof column.label === 'string' ? column.label : '') ||
    'col';
  return `${tableName}-${identifier}-${index}`;
}

export function Table<TData>(props: TableProps<TData>) {
  const {
    bodyHeader,
    tableClassName,
    columns,
    data: rawData,
    selection,
    status,
    sort,
    error,
    name: tableName,
    fetchNextPage,
    isFetchingNextPage,
    isFetching = false,
    rowActions,
    bulkActions,
    hasNextPage,
    groupBy,
    onRowClick,
    loadingRowCount = 25,
    isTableView = false,
    primaryAction,
    resize = false,
    search: controlledSearch,
    rowClassName,
    hideTableHeader,
    noData,
    rowHeight = ROW_HEIGHT,
    keyboardActions,
    keyboardNavigation,
    sortableColumns,
    responsive = true,
    infiniteScroll,
    maxHeight,
    minHeight,
  } = props;

  // Extract infinite scroll configuration with defaults
  const infiniteScrollConfig = {
    minItemsThreshold: infiniteScroll?.minItemsThreshold ?? 10,
    disabled: infiniteScroll?.disabled ?? false,
  };

  const [inspecting, setInspecting] = useState<number | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const tableId = useId();
  const [expandedGroups, setExpandedGroups] = useState(
    groupBy?.map(group => group.label) || []
  );
  /**
   * Generates unique row keys for React virtualization.
   *
   * Strategy:
   * 1. Use item.key if unique within dataset
   * 2. For duplicates, append occurrence counter for deterministic uniqueness
   * 3. Fallback to index for items without keys
   *
   * Why Key-Based Selection:
   * Selection system uses _key to track selected rows. Unstable keys break
   * selection when data is re-sorted or filtered.
   *
   * Known Issue:
   * Backend may return duplicate keys for nuclei template vulnerabilities.
   * This is a workaround until backend ensures unique identifiers.
   *
   * @see selection.value - stores selected row._key values
   * @see handleRowClick - uses _key for selection toggling
   */
  const indexedData: InternalTData<TData>[] = useMemo(() => {
    const keyOccurrences = new Map<string, number>();

    return rawData.map((item, _index) => {
      const itemKey = (item as Record<string, unknown>)?.key;
      const keyValue = typeof itemKey === 'string' ? itemKey : undefined;

      let generatedKey: string;

      if (keyValue) {
        // Track occurrence count for this key
        const occurrenceIndex = keyOccurrences.get(keyValue) ?? 0;
        keyOccurrences.set(keyValue, occurrenceIndex + 1);

        // Append occurrence index only for duplicates
        if (occurrenceIndex > 0) {
          generatedKey = `${keyValue}__dup${occurrenceIndex}`;

          // Warn developers in development mode only
          console.warn(
            `Table "${tableName}": Duplicate key detected - "${keyValue}" ` +
              `(occurrence ${occurrenceIndex + 1}). ` +
              `This may indicate a backend data quality issue.`,
            { rowIndex: _index, item }
          );
        } else {
          generatedKey = keyValue;
        }
      } else {
        // Fallback to index for items without keys
        generatedKey = `__row${_index}`;
      }

      return {
        ...item,
        _idx: _index.toString(),
        _key: generatedKey,
      } as InternalTData<TData>;
    });
  }, [rawData, tableName]);

  // Hover state management (lifted from TableBody for architecture fix)
  // Single source of truth for which row is currently hovered
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  // Filter the data based on the groupBy value
  const groupedData = useMemo(() => {
    const groupedData =
      groupBy && status === 'success'
        ? groupBy.reduce((acc, group) => {
            const filteredData = indexedData.filter(item => group.filter(item));
            // Add the group label only when there is filtered data available
            const newData =
              filteredData.length > 0
                ? [
                    ...acc,
                    {
                      _label: group.label,
                      _type: 'colgroup',
                      _idx: -1,
                      _icon: group.icon,
                    },
                    ...(expandedGroups.includes(group.label)
                      ? filteredData
                      : []),
                  ]
                : acc;

            return newData as InternalTData<TData>[];
          }, [] as InternalTData<TData>[])
        : indexedData;

    return groupedData;
  }, [groupBy, indexedData, expandedGroups, status]);

  // Responsive column management
  // Calculate correct reserved width based on actual checkbox and actions columns
  // Use props directly since enableCheckbox is defined later
  const reservedWidth =
    (!selection?.hide ? CELL_WIDTHS.checkbox.fixedWidth : 0) +
    (rowActions ? CELL_WIDTHS.actions.fixedWidth : 0);

  const { visibleColumns, isResponsive } = useResponsiveColumns(
    columns,
    tableContainerRef,
    {
      enabled: responsive,
      reservedWidth, // Pass actual reserved width (26px checkbox + 40px actions = 66px)
    }
  );

  // Use visibleColumns instead of columns for rendering
  const columnsToRender = responsive ? visibleColumns : columns;

  // Memoize column styles to prevent recalculation on every render
  // Uses centralized applyColumnWidth utility for consistent width application
  const columnStyles = useMemo(() => {
    return columnsToRender.map(column =>
      applyColumnWidth(column, {
        tableLayoutFixed: true,
        responsiveMode: isResponsive,
      })
    );
  }, [columnsToRender, isResponsive]);

  const parentRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isTableView) {
      parentRef.current = document.getElementById('body');
    } else {
      // Use scrollableRef first (the actual scrollable element), fallback to tableId
      parentRef.current =
        scrollableRef.current || document.getElementById(tableId);
    }
  }, [tableId, isTableView]);

  useScroll(parentRef, fetchNextPage, hasNextPage);

  const [isBulkSelectionEnabled, setIsBulkSelectionEnabled] = useState(false);

  // ======================================================================
  // SELECTION STATE MANAGEMENT (Controlled Component Pattern)
  // ======================================================================
  // Fix for infinite loop and flickering caused by useStorage's
  // bidirectional sync. This implementation matches Customer Management's
  // working pattern using standard React controlled component approach.
  //
  // Key changes:
  // - Removed useStorage (was designed for persistence, not controlled state)
  // - Use controlled pattern when parent provides selection.value
  // - Use uncontrolled pattern when parent doesn't provide state
  // - Single source of truth: parent's state (no bidirectional sync)
  // ======================================================================

  const isControlled = selection?.value !== undefined;

  // Internal state for uncontrolled mode (when parent doesn't provide state)
  const [internalSelectedRows, setInternalSelectedRows] = useState<string[]>(
    []
  );

  // Use parent's state in controlled mode, internal state in uncontrolled mode
  const selectedRows = isControlled
    ? selection?.value || []
    : internalSelectedRows;

  // Sync internal state when parent state changes (controlled mode only)
  // This ensures checkbox UI stays in sync with parent's state
  useEffect(() => {
    if (isControlled && selection?.value) {
      setInternalSelectedRows(selection.value);
    }
  }, [isControlled, selection?.value]);

  // Unified setter for both controlled and uncontrolled modes
  const setSelectedRows = useCallback(
    (value: string[] | ((prev: string[]) => string[])) => {
      // Resolve function-based updates (e.g., prev => [...prev, newItem])
      const newValue =
        typeof value === 'function' ? value(selectedRows) : value;

      // Update internal state (used in uncontrolled mode)
      setInternalSelectedRows(newValue);

      // Notify parent if callback provided (controlled mode)
      // Parent will update its state, which flows back via selection.value
      if (selection?.onChange) {
        selection.onChange(newValue);
      }
    },
    [selectedRows, selection]
  );

  // NOTE: lastSelectedRow was used for bulk selection with Shift key (now disabled)
  // Keeping state and setter for potential future re-implementation of bulk selection
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [lastSelectedRow, setLastSelectedRow] = useState<number>();

  const enableCheckbox = !selection?.hide;
  const showSelectAllCheckbox = Boolean(selection?.selectAll ?? true);
  const isLoading = status === 'pending' || (isFetching && !isFetchingNextPage);

  // Calculate minimum table width for responsive mode
  // This ensures we don't compress columns below their minWidth
  const minTableWidth = useMemo(() => {
    if (!isResponsive) {
      return undefined;
    }

    // Calculate minimum width needed (sum of all minWidths)
    const columnsWidth = columnsToRender.reduce((sum, col) => {
      const width =
        col.fixedWidth || col.isExpandable?.minWidth || col.minWidth || 100;
      return sum + width;
    }, 0);

    // Add reserved widths for UI elements
    const checkboxWidth = enableCheckbox ? CELL_WIDTHS.checkbox.fixedWidth : 0;
    const actionsWidth = rowActions ? CELL_WIDTHS.actions.fixedWidth : 0;

    return columnsWidth + checkboxWidth + actionsWidth;
  }, [columnsToRender, isResponsive, enableCheckbox, rowActions]);

  /**
   * Calculate buffer row count based on pagination state.
   *
   * Buffer rows extend the virtual scroll area to prevent the last data row
   * from being cut off by bottom padding. They are only rendered when:
   * - hasNextPage is false (no more data to load)
   * - isLoading is false (not currently fetching)
   * - groupedData has items (not empty state)
   *
   * Buffer rows are invisible spacers that increase getTotalSize() by
   * SCROLL_BUFFER_ROW_COUNT * rowHeight, allowing users to scroll past
   * the last data row without it being obscured by padding.
   */
  const bufferRowCount =
    !hasNextPage && !isLoading && groupedData.length > 0
      ? SCROLL_BUFFER_ROW_COUNT
      : 0;

  /**
   * Virtualizer for efficient rendering of large datasets.
   *
   * Uses measureElement in addition to estimateSize to fix zoom-related bugs.
   * At extreme zoom levels (60% zoom OUT), estimateSize alone caused row heights
   * to be calculated as 8996px instead of 52px due to CSS pixel multiplication.
   *
   * measureElement uses offsetHeight which returns zoom-independent measurements
   * from actual DOM elements, overriding the estimateSize calculation.
   *
   * Performance: ~1-2ms overhead compared to estimateSize only (negligible)
   * Zoom Range: Tested 50%-200% zoom levels
   *
   * @see https://tanstack.com/virtual/v3/docs/api/virtualizer#measureelement
   */
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual returns non-memoizable functions (expected behavior)
  const virtualizer = useVirtualizer({
    // Add buffer rows to count so getTotalSize() includes buffer space
    count: isLoading ? loadingRowCount : groupedData.length + bufferRowCount,
    getScrollElement: useCallback(() => {
      return parentRef.current;
    }, []),
    estimateSize: useCallback(() => rowHeight, [rowHeight]),
    measureElement: useCallback(
      (el: Element) => {
        // Use offsetHeight for accurate measurement at all zoom levels
        // This is more reliable than getBoundingClientRect().height
        const measured =
          el instanceof HTMLElement ? el.offsetHeight : rowHeight;

        // ZOOM FIX: At extreme zoom levels (67%, 88%, 94%), browsers may report
        // heights slightly different from the intended rowHeight due to subpixel rendering.
        // Example: At 67% zoom with rowHeight=64px:
        //   - Intended: 64px
        //   - Browser reports: 63.89px or 64.11px
        //
        // If we use the browser's measurement, TanStack Virtual will accumulate these
        // tiny differences across hundreds of rows, causing the virtualizer's total size
        // to drift from reality. This manifests as:
        //   - Scroll position jumping
        //   - Incorrect "after" padding calculations
        //   - Last rows appearing cut off
        //
        // Solution: If the measured height is within 2px of rowHeight, snap to rowHeight.
        // This prevents accumulation of subpixel errors while still allowing legitimate
        // height variations (e.g., expanded rows, error states).
        if (Math.abs(measured - rowHeight) <= 2) {
          return rowHeight;
        }
        return measured;
      },
      [rowHeight]
    ),
    overscan: 5,
    scrollMargin: 100,
  });

  function toggleExpandedGroup(group?: string) {
    if (group) {
      setExpandedGroups(expandedGroups => {
        if (expandedGroups.includes(group)) {
          return expandedGroups.filter(g => g !== group);
        }

        return [...expandedGroups, group];
      });
    }
  }

  const items = virtualizer.getVirtualItems();
  const [before, after] =
    items.length > 0
      ? [
          notUndefined(items[0]).start - virtualizer.options.scrollMargin,
          virtualizer.getTotalSize() -
            notUndefined(items[items.length - 1]).end,
        ]
      : [0, 0];

  const isAllRowSelected = useMemo(() => {
    if (status === 'success') {
      return rawData.length > 0
        ? selectedRows.length === rawData.length
        : false;
    }

    return false;
  }, [rawData.length, selectedRows.length, status]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Shift') {
        setIsBulkSelectionEnabled(true);
      }
    }

    function handleKeyUp(event: KeyboardEvent) {
      if (event.key === 'Shift') {
        setIsBulkSelectionEnabled(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (!keyboardActions) return;
      const { codes, onKeyboardAction, altKey } = keyboardActions;

      // Handle selected rows (by key) or inspecting row (by index)
      let data: TData[] = [];

      if (selectedRows.length > 0) {
        // Look up selected rows by their _key
        data = selectedRows
          .map(key => groupedData.find(row => row._key === key))
          .filter(Boolean) as TData[];
      } else if (inspecting !== null) {
        // For inspecting, use the index to look up in rawData
        const inspectedRow = rawData[inspecting];
        if (inspectedRow) {
          data = [inspectedRow];
        }
      }

      if (codes?.length > 0 && data.length > 0) {
        const isAltKey = altKey ? event.altKey : true;
        if (isAltKey && codes.includes(event.code)) {
          onKeyboardAction(event.code, data);
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedRows, inspecting, rawData, groupedData, keyboardActions]);

  const handleRowClick = useCallback(
    (idx: number, event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
      const target = event.target as HTMLElement;
      const clickedCheckbox = target?.closest?.('[data-checkbox]');

      if (enableCheckbox && clickedCheckbox) {
        // Get the row data to access its _key
        const rowData = groupedData[idx];
        const rowKey = rowData?._key || idx.toString();

        // Simple toggle selection (disable bulk/shift selection for now)
        // Note: Bulk selection with Shift key is temporarily disabled to fix tag operations
        // TODO: Refactor bulk selection to work with row keys instead of indices
        setSelectedRows(selectedRows => {
          const isChecked = selectedRows.includes(rowKey);

          if (isChecked) {
            // Remove from selection
            return selectedRows.filter(key => key !== rowKey);
          } else {
            // Add to selection
            return [...selectedRows, rowKey];
          }
        });

        setLastSelectedRow(idx);
      } else {
        setInspecting(Number(idx));
        onRowClick?.(rawData[Number(idx)], Number(idx), event);
      }
    },
    [
      enableCheckbox,
      groupedData,
      setSelectedRows,
      selectedRows,
      setLastSelectedRow,
      setInspecting,
      onRowClick,
      rawData,
    ]
  );

  function handleSelectAll() {
    if (isAllRowSelected) {
      setSelectedRows([]);
    } else {
      // Select all visible rows by their _key
      const allKeys = groupedData
        .filter(row => row._type !== 'colgroup') // Exclude group headers
        .map(row => row._key);
      setSelectedRows(allKeys);
    }
  }

  const { keyboardListeners } = useGlobalState();

  useEffect(() => {
    if (!keyboardNavigation) {
      return;
    }
    const ArrowDownListener = {
      ArrowDown: (event: KeyboardEvent) => {
        event.preventDefault();
        const nextIndex =
          inspecting === null || inspecting + 1 >= groupedData.length
            ? 0
            : inspecting + 1;

        // Ensure the row is in view
        virtualizer.scrollToIndex(nextIndex, {
          align: 'center',
        });

        setInspecting(nextIndex);
        onRowClick?.(rawData[nextIndex], Number(nextIndex));
      },
    };
    if (groupedData.length > 0) {
      if (inspecting === null) {
        keyboardListeners.add(tableName, ArrowDownListener);
      } else {
        keyboardListeners.add(tableName, {
          ...ArrowDownListener,

          ArrowUp: (event: KeyboardEvent) => {
            event.preventDefault();
            const nextIndex =
              inspecting === null ? 0 : Math.max(0, inspecting - 1);

            // Ensure the row is in view
            virtualizer.scrollToIndex(nextIndex, {
              align: 'center',
            });

            setInspecting(nextIndex);
            onRowClick?.(rawData[nextIndex], Number(nextIndex));
          },
        });
      }
    }
  }, [tableName, groupedData.length, inspecting, keyboardNavigation]);

  useEffect(() => {
    return () => {
      keyboardListeners.remove(tableName);
    };
  }, []);

  const isError = status === 'error';
  // BUGFIX: Don't show empty state while fetching with keepPreviousData
  // When using keepPreviousData, isFetching=true with status='success' and rawData=[]
  // This prevents the blank table bug when clearing filters that returned no results
  const isEmpty = status === 'success' && rawData.length === 0 && !isFetching;
  const isLoadingOrHaveData = !isError && !isEmpty;
  const columnsLength =
    columnsToRender.length + (enableCheckbox ? 1 : 0) + (rowActions ? 1 : 0);

  const { parsedActions, parsedPrimaryAction } = useMemo(() => {
    // Look up selected rows by their _key instead of treating as indices
    const selectedRowData = selectedRows
      .map(key => groupedData.find(row => row._key === key))
      .filter(Boolean) as TData[];

    return {
      parsedActions: bulkActions?.(selectedRowData),
      parsedPrimaryAction: primaryAction?.(selectedRowData),
    };
  }, [selectedRows, groupedData, bulkActions, primaryAction]);

  return (
    <>
      <div
        data-testid={`table-${tableName}`}
        id={tableId}
        className={cn(!isTableView && 'h-full pb-2')}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {(bodyHeader || parsedActions || parsedPrimaryAction) && (
          <RenderHeaderExtraContentSection>
            <div className="flex flex-col justify-between gap-4 lg:flex-row">
              {bodyHeader}
              <div className="flex space-x-4">
                {controlledSearch && (
                  <Input
                    name="search"
                    placeholder={`Go to ${tableName}`}
                    className="bg-header-light h-11 w-72 justify-end rounded-sm border-gray-900 p-2 text-sm text-white ring-0 md:w-64"
                    value={controlledSearch.value || ''}
                    onChange={e => controlledSearch.onChange(e.target.value)}
                    startIcon={
                      <MagnifyingGlassIcon className="text-default-light size-5 stroke-2" />
                    }
                    endIcon={
                      <Tooltip
                        title={
                          <span className="text-md">
                            Press{' '}
                            <Slash className="inline h-4 w-2.5 rounded-sm border-[1.5px] border-gray-500 text-lg text-gray-400" />{' '}
                            to focus search
                          </span>
                        }
                      >
                        <InformationCircleIcon className="text-default-light size-6" />
                      </Tooltip>
                    }
                  />
                )}

                <div className="flex h-full flex-nowrap">
                  {parsedPrimaryAction && (
                    <Button
                      {...parsedPrimaryAction}
                      styleType="header"
                      className="rounded-none rounded-l-[2px]"
                    />
                  )}

                  {parsedActions && (
                    <div>
                      <Tooltip
                        title={
                          selectedRows.length === 0
                            ? `No ${tableName} selected.`
                            : ''
                        }
                      >
                        <Dropdown
                          disabled={selectedRows.length === 0}
                          className={cn(
                            parsedPrimaryAction &&
                              'bg-header-dark disabled:bg-header-dark relative h-full rounded-none rounded-r-[2px] disabled:cursor-not-allowed'
                          )}
                          styleType="header"
                          endIcon={
                            <ChevronDownIcon className="size-3 stroke-[4px]" />
                          }
                          {...parsedActions}
                        ></Dropdown>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </RenderHeaderExtraContentSection>
        )}
        {/* Height-constrained container for ALL content states */}
        <div
          className={cn(
            'flex flex-col',
            !isTableView && 'flex-1 overflow-hidden'
          )}
          style={{
            ...(maxHeight && { maxHeight: `${maxHeight}px` }),
            ...(minHeight && { minHeight: `${minHeight}px` }),
          }}
        >
          {isError && tableName !== 'assets' && tableName !== 'agents' && (
            <NoData
              title={`Unable to retrieve your ${tableName}`}
              description={
                error && error.message ? error.message : 'Something went wrong!'
              }
              icon={
                <ExclamationCircleIcon className={`size-20 text-red-400`} />
              }
            />
          )}
          {isEmpty ||
          (isError && (tableName === 'assets' || tableName === 'agents')) ? (
            <NoData
              title={noData?.title || `No ${startCase(tableName)} found`}
              description={noData?.description}
              icon={noData?.icon}
              styleType={noData?.styleType}
              action={noData?.action}
            />
          ) : null}
          {isLoadingOrHaveData && (
            <div
              ref={el => {
                scrollableRef.current = el;
                tableContainerRef.current = el;
              }}
              className={cn(!isTableView && 'flex-1 overflow-auto')}
            >
              <table
                ref={tableRef}
                tabIndex={0}
                data-count={indexedData.length}
                className={cn(
                  'relative w-full border-collapse overflow-visible focus:outline-none',
                  isBulkSelectionEnabled && 'select-none',
                  `border-0 bg-transparent [&__tr_td_div:first]:border-t-4 [&_th>div:not([data-sortable])]:border-0 [&_thead_tr]:bg-transparent`,
                  tableClassName
                )}
                style={{
                  ...(minTableWidth && { minWidth: `${minTableWidth}px` }),
                  // Use 'fixed' layout when columns have width constraints to ensure browser respects fixedWidth/maxWidth
                  // Use 'auto' layout only when columns don't specify widths (allows browser to size based on content)
                  tableLayout: 'fixed',
                }}
              >
                {/* Add colgroup for fixed column widths */}
                <colgroup>
                  {enableCheckbox && (
                    <col
                      style={{
                        width: `${CELL_WIDTHS.checkbox.fixedWidth}px`,
                        minWidth: `${CELL_WIDTHS.checkbox.minWidth}px`,
                        maxWidth: `${CELL_WIDTHS.checkbox.maxWidth}px`,
                      }}
                    />
                  )}
                  {/**
                   * Apply column width constraints to <col> elements using centralized utility.
                   *
                   * CRITICAL FIX: Now uses applyColumnWidth() to ensure isExpandable.minWidth
                   * applies to <col> elements (not just header span). This fixes the issue where
                   * expandable columns could shrink below minWidth with tableLayout: 'fixed'.
                   *
                   * @see applyColumnWidth for width constraint application logic
                   */}
                  {columnStyles.map((style, idx) => (
                    <col key={idx} style={style} />
                  ))}
                  {rowActions && (
                    <col
                      style={{
                        width: `${CELL_WIDTHS.actions.fixedWidth}px`,
                        minWidth: `${CELL_WIDTHS.actions.minWidth}px`,
                        maxWidth: `${CELL_WIDTHS.actions.maxWidth}px`,
                      }}
                    />
                  )}
                </colgroup>
                <thead
                  className={'sticky z-10'}
                  style={{
                    top: 0,
                  }}
                >
                  <tr className="relative overflow-visible">
                    {enableCheckbox && (
                      <Th
                        column={
                          {
                            id: '',
                            label: '',
                            fixedWidth: CELL_WIDTHS.checkbox.fixedWidth,
                          } as Column<TData>
                        }
                        align="center"
                        storageKey={`${tableName}-checkbox`}
                        className={hideTableHeader ? 'p-0' : ''}
                      >
                        {!hideTableHeader && showSelectAllCheckbox && (
                          <label className={'cursor-pointer'}>
                            <input
                              type="checkbox"
                              onChange={handleSelectAll}
                              className="hidden"
                              checked={isAllRowSelected}
                            />
                            <Checkbox isChecked={isAllRowSelected} />
                          </label>
                        )}
                      </Th>
                    )}
                    {columnsToRender.map((column, colIdx) => {
                      const sorting =
                        column.sorting === undefined ? true : column.sorting;
                      const allowSorting = !hideTableHeader && sort && sorting;

                      // Handle column header click for sorting
                      const handleHeaderClick = () => {
                        if (!allowSorting) return;

                        const isCurrentColumn = column.id === sort.orderBy;
                        if (isCurrentColumn) {
                          // Toggle direction for current sort column
                          sort.onSort({
                            orderBy: column.id as keyof TData,
                            descending: !sort.descending,
                          });
                        } else {
                          // New column - default to descending
                          sort.onSort({
                            orderBy: column.id as keyof TData,
                            descending: true,
                          });
                        }
                      };

                      return (
                        <Th
                          key={getColumnKey(tableName, column, colIdx)}
                          className={cn(
                            column.className,
                            hideTableHeader ? 'p-0' : '',
                            sort && 'group hover:cursor-pointer'
                          )}
                          column={column}
                          align={column.align}
                          resize={resize}
                          storageKey={getColumnKey(tableName, column, colIdx)}
                          onClick={allowSorting ? handleHeaderClick : undefined}
                        >
                          <span className={'flex items-center gap-2'}>
                            {!hideTableHeader && column.label}
                            {!hideTableHeader && column.info && (
                              <Tooltip title={column.info}>
                                <InformationCircleIcon className="text-chariot-text-secondary size-4 shrink-0 cursor-pointer stroke-2" />
                              </Tooltip>
                            )}
                            {allowSorting && (
                              <SortColumn column={column} sort={sort} />
                            )}
                          </span>
                        </Th>
                      );
                    })}
                    {rowActions && (
                      <Th
                        column={
                          {
                            id: '',
                            label: '',
                            fixedWidth: CELL_WIDTHS.actions.fixedWidth,
                          } as Column<TData>
                        }
                        storageKey={`${tableName}-actions`}
                        className="pr-2"
                      >
                        {sortableColumns && (
                          <div data-sortable className="flex justify-center">
                            <SortableColumns
                              styleType="icon"
                              placeholder=""
                              defaultConfig={sortableColumns.defaultConfig}
                              position="right"
                              activeColumns={sortableColumns.activeColumns}
                              onChangeActiveColumns={
                                sortableColumns.setActiveColumns
                              }
                              icon={<Cog8ToothIcon className="size-6" />}
                            />
                          </div>
                        )}
                      </Th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {before > 0 && (
                    <tr>
                      <td colSpan={columnsLength} style={{ height: before }} />
                    </tr>
                  )}
                  {items.map((virtualRow, rowIndex) => {
                    const item = groupedData[virtualRow.index];

                    // Check if this is a buffer row (beyond actual data)
                    if (!item && virtualRow.index >= groupedData.length) {
                      /**
                       * Render invisible spacer row for scroll buffer.
                       *
                       * - Uses fixed height (no measurement needed since height is constant)
                       * - Hidden from screen readers via aria-hidden
                       * - Key includes groupedData.length to prevent collision during data changes
                       */
                      return (
                        <tr
                          key={`buffer-${virtualRow.index}-${groupedData.length}`}
                          data-buffer-row
                          aria-hidden="true"
                          style={{
                            height: `${rowHeight}px`,
                          }}
                        >
                          <td colSpan={columnsLength} style={{ padding: 0 }} />
                        </tr>
                      );
                    }

                    // Skip undefined items that aren't buffer rows
                    if (!item) return null;

                    const isChecked = item._key
                      ? selectedRows.includes(item._key)
                      : false;
                    const isLastRow =
                      virtualRow.index === groupedData.length - 1;

                    return (
                      <TableBody
                        key={item._key}
                        ref={el => {
                          rowRefs.current[virtualRow.index] = el;
                          // Notify virtualizer to measure this element
                          if (el) {
                            virtualizer.measureElement(el);
                          }
                        }}
                        virtualRow={virtualRow}
                        isChecked={isChecked}
                        isLastRow={isLastRow}
                        rowClassName={rowClassName}
                        isClickable={Boolean(onRowClick)}
                        columns={columnsToRender}
                        selectedRows={selectedRows}
                        data={groupedData}
                        rowActions={rowActions}
                        rowData={item}
                        rowIndex={rowIndex}
                        handleRowClick={handleRowClick}
                        enableCheckbox={enableCheckbox}
                        isLoading={isLoading}
                        expandedGroups={expandedGroups}
                        toggleExpandedGroup={toggleExpandedGroup}
                        groupBy={groupBy}
                        sort={sort}
                        inspecting={inspecting}
                        rowHeight={rowHeight}
                        tabIndex={virtualRow.index === inspecting ? 0 : -1}
                        hoveredRowIndex={hoveredRowIndex}
                        setHoveredRowIndex={setHoveredRowIndex}
                      />
                    );
                  })}
                  {after > 0 && (
                    <tr>
                      <td colSpan={columnsLength} style={{ height: after }} />
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Unified infinite scroll indicator - shows loading or scroll hint */}
              {!infiniteScrollConfig.disabled && (
                <InfiniteScrollIndicator
                  isLoading={Boolean(isFetchingNextPage)}
                  hasMore={hasNextPage === true}
                  itemCount={rawData?.length || 0}
                  minItemsThreshold={infiniteScrollConfig.minItemsThreshold}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Th<TData>(props: {
  className?: string;
  column: Column<TData>; // NEW: Pass full column config
  children: ReactNode;
  storageKey: string;
  align?: CellAlignment;
  resize?: boolean;
  onClick?: () => void; // Support click handler for sorting
}) {
  const ref = useRef<HTMLTableCellElement>(null);
  const { column, resize = false, storageKey, ...restProps } = props;

  // Get minWidth from column configuration
  const minWidth =
    column.fixedWidth ||
    column.isExpandable?.minWidth ||
    column.minWidth ||
    100;

  const { size, onMouseDown } = useResize({
    elRef: ref,
    minWidth: minWidth,
    storageKey: storageKey,
  });

  // Use centralized width application
  const widthStyle = applyColumnWidth(column, {
    resize: resize,
    storedWidth: size.x,
  });

  return (
    <th
      {...restProps}
      className={cn(
        'bg-chariot-background-primary relative py-3 pr-1 pl-2 text-left text-sm font-semibold text-nowrap',
        props.align === 'center' &&
          'pr-3 text-center [&>span]:flex [&>span]:w-full [&>span]:justify-center',
        props.align === 'right' &&
          'text-right [&>span]:flex [&>span]:w-full [&>span]:justify-end',
        props.className
      )}
      style={widthStyle}
      ref={ref}
    >
      <div className="th-top-border border-default absolute top-0 left-0 w-full border-b border-solid" />
      <div className="th-bottom-border bg-chariot-stroke absolute bottom-0 left-0 h-[2px] w-full" />
      {props.children}
      {resize && (
        <div
          className="resize-el bg-default-dark absolute top-1/3 right-px h-1/3 w-0.5 select-none hover:cursor-col-resize"
          onMouseDown={onMouseDown}
        ></div>
      )}
    </th>
  );
}
